import React, { useEffect, useMemo, useState } from "react";
import api from "../api/api";
import { FileBarChart, Filter, X, Download, User, Calendar } from "lucide-react";

type Row = {
  id: string;
  type: "LEAVE" | "SHORT_LEAVE" | "OVERTIME_BEFORE" | "OVERTIME_AFTER";
  leaveKind?: "ANNUAL" | "CASUAL" | null;
  startDate: string;
  endDate: string | null;
  days: number | null;
  hours: number | null;
  reason: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  decidedAt: string | null;
  createdAt: string;
  employee: { id: string; name: string; email: string; department: string | null };
  manager?: { name: string } | null;
};

type PerEmp = {
  name: string; department: string | null; count: number;
  leaveDaysApproved: number; leaveDaysPending: number;
  shortLeaveCount: number; shortLeaveCountApproved: number;
  overtimeHoursApproved: number; overtimeHoursPending: number;
};

type Summary = {
  totalRecords: number;
  leaveDaysApproved: number; leaveDaysPending: number; leaveCount: number;
  shortLeaveCount: number; shortLeaveCountApproved: number;
  overtimeHoursApproved: number; overtimeHoursPending: number; overtimeCount: number;
  perEmployee: PerEmp[];
};

const STATUS_COLOR: Record<string, string> = {
  PENDING:  "bg-amber-50 text-amber-700",
  APPROVED: "bg-green-50 text-green-700",
  REJECTED: "bg-red-50 text-red-700",
};

const TYPE_LABEL: Record<string, string> = {
  LEAVE: "🏖️ Leave",
  SHORT_LEAVE: "🕐 Short Leave",
  OVERTIME_BEFORE: "⏰ Overtime (pre)",
  OVERTIME_AFTER:  "⌛ Overtime (post)",
};

function typeLabel(r: { type: string; leaveKind?: string | null }): string {
  if (r.type === "LEAVE") return r.leaveKind === "CASUAL" ? "🏝️ Casual" : "🏖️ Annual";
  return TYPE_LABEL[r.type];
}

function firstOfMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function lastOfMonth(): string {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, "0")}-${String(last.getDate()).padStart(2, "0")}`;
}

export default function RequestsReport() {
  const [rows, setRows] = useState<Row[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [employeeId, setEmployeeId] = useState("");
  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [employees, setEmployees] = useState<Array<{ id: string; name: string; department: string | null }>>([]);

  useEffect(() => {
    api.get("/users").then(r => {
      const list = (r.data || []).filter((u: any) => u.isActive !== false && !u.isDeleted);
      setEmployees(list.map((u: any) => ({ id: u.id, name: u.name, department: u.department })));
    }).catch(() => {});
  }, []);

  const departments = useMemo(() => {
    const set = new Set<string>();
    employees.forEach(e => e.department && set.add(e.department));
    return Array.from(set).sort();
  }, [employees]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (employeeId) params.set("employeeId", employeeId);
    if (status) params.set("status", status);
    if (department) params.set("department", department);
    if (typeFilter) params.set("type", typeFilter);
    api.get(`/requests/report?${params.toString()}`)
      .then(r => { setRows(r.data.rows || []); setSummary(r.data.summary || null); })
      .catch(() => { setRows([]); setSummary(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [from, to, employeeId, status, department, typeFilter]);

  const reset = () => {
    setFrom(firstOfMonth()); setTo(lastOfMonth());
    setEmployeeId(""); setStatus(""); setDepartment(""); setTypeFilter("");
  };

  const exportCsv = () => {
    const header = ["Date", "Employee", "Department", "Type", "Days", "Hours", "Status", "Reason", "Decided By"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        new Date(r.startDate).toISOString().slice(0, 10),
        r.employee.name,
        r.employee.department || "",
        typeLabel(r),
        String(r.days || 0),
        String(r.hours || 0),
        r.status,
        (r.reason || "").replace(/"/g, '""'),
        r.manager?.name || "",
      ];
      lines.push(cells.map(c => `"${c}"`).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `requests-report-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showLeave = !typeFilter || typeFilter === "LEAVE";
  const showShort = !typeFilter || typeFilter === "SHORT_LEAVE";
  const showOT = !typeFilter || typeFilter === "OVERTIME";

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileBarChart className="text-indigo-600" size={26}/>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Requests Report</h1>
            <p className="text-sm text-gray-500">Leave · Short Leave · Overtime — filterable</p>
          </div>
        </div>
        <button onClick={exportCsv} disabled={rows.length === 0}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">
          <Download size={14}/> Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">Total Records</div>
            <div className="text-2xl font-bold text-gray-900">{summary.totalRecords}</div>
            <div className="text-[11px] text-gray-400 mt-1">
              {summary.leaveCount}L · {summary.shortLeaveCount}SL · {summary.overtimeCount}OT
            </div>
          </div>
          {showLeave && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-xs text-blue-700 mb-1">🏖️ Leave Days (Approved)</div>
              <div className="text-2xl font-bold text-blue-800">{summary.leaveDaysApproved.toFixed(1)}</div>
              <div className="text-[11px] text-blue-500 mt-1">+ {summary.leaveDaysPending.toFixed(1)} pending</div>
            </div>
          )}
          {showShort && (
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4">
              <div className="text-xs text-cyan-700 mb-1">🕐 Short Leaves (Approved)</div>
              <div className="text-2xl font-bold text-cyan-800">{summary.shortLeaveCountApproved}</div>
              <div className="text-[11px] text-cyan-500 mt-1">Total: {summary.shortLeaveCount} · = {(summary.shortLeaveCountApproved * 2).toFixed(0)}h</div>
            </div>
          )}
          {showOT && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="text-xs text-orange-700 mb-1">⏰ Overtime Hours (Approved)</div>
              <div className="text-2xl font-bold text-orange-800">{summary.overtimeHoursApproved.toFixed(1)}</div>
              <div className="text-[11px] text-orange-500 mt-1">+ {summary.overtimeHoursPending.toFixed(1)} pending</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
          <Filter size={14}/> Filters
          <button onClick={reset} className="ml-auto text-xs text-blue-600 hover:underline flex items-center gap-1">
            <X size={12}/> Reset
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"/>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"/>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              <option value="">All</option>
              <option value="LEAVE">🏖️ Leave</option>
              <option value="SHORT_LEAVE">🕐 Short Leave</option>
              <option value="OVERTIME">⏰ Overtime</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Employee</label>
            <select value={employeeId} onChange={e => setEmployeeId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              <option value="">All</option>
              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Department</label>
            <select value={department} onChange={e => setDepartment(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              <option value="">All</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              <option value="">All</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Per-Employee Summary */}
      {summary && summary.perEmployee.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-5">
          <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-700 bg-gray-50">
            📊 Per-Employee Breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Employee</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Department</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Total</th>
                  {showLeave && <th className="text-right px-4 py-2 font-medium text-blue-700">🏖️ Leave (days)</th>}
                  {showShort && <th className="text-right px-4 py-2 font-medium text-cyan-700">🕐 Short (count)</th>}
                  {showOT && <th className="text-right px-4 py-2 font-medium text-orange-700">⏰ OT (hours)</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {summary.perEmployee.map((e, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 font-medium text-gray-800">{e.name}</td>
                    <td className="px-4 py-2 text-gray-600">{e.department || "—"}</td>
                    <td className="px-4 py-2 text-right text-gray-700 font-semibold">{e.count}</td>
                    {showLeave && (
                      <td className="px-4 py-2 text-right text-blue-700">
                        <span className="font-semibold">{e.leaveDaysApproved.toFixed(1)}</span>
                        {e.leaveDaysPending > 0 && <span className="text-[11px] text-amber-600 ml-1">+{e.leaveDaysPending.toFixed(1)}p</span>}
                      </td>
                    )}
                    {showShort && (
                      <td className="px-4 py-2 text-right text-cyan-700">
                        <span className="font-semibold">{e.shortLeaveCountApproved}</span>
                        {e.shortLeaveCount > e.shortLeaveCountApproved && (
                          <span className="text-[11px] text-gray-500 ml-1">({e.shortLeaveCount})</span>
                        )}
                      </td>
                    )}
                    {showOT && (
                      <td className="px-4 py-2 text-right text-orange-700">
                        <span className="font-semibold">{e.overtimeHoursApproved.toFixed(1)}</span>
                        {e.overtimeHoursPending > 0 && <span className="text-[11px] text-amber-600 ml-1">+{e.overtimeHoursPending.toFixed(1)}p</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Details Table */}
      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
          No records match these filters.
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-700 bg-gray-50">
            📋 Details ({rows.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Employee</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Department</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Type</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Amount</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const amount = r.type === "LEAVE"
                    ? `${r.days} day${r.days === 1 ? "" : "s"}`
                    : r.type === "SHORT_LEAVE"
                      ? "2 hours"
                      : `${r.hours} hr${r.hours === 1 ? "" : "s"}`;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                        <Calendar size={12} className="inline text-gray-400 mr-1"/>
                        {new Date(r.startDate).toLocaleDateString()}
                        {r.endDate && r.endDate !== r.startDate && ` → ${new Date(r.endDate).toLocaleDateString()}`}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900">
                        <User size={12} className="inline text-gray-400 mr-1"/>
                        {r.employee.name}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{r.employee.department || "—"}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 whitespace-nowrap">{typeLabel(r)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-gray-800">{amount}</td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${STATUS_COLOR[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2 max-w-xs truncate text-gray-600" title={r.reason}>
                        {r.reason || <span className="text-gray-300">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
